/**
 * Desktop sort combobox (progressive enhancement).
 *
 * The native <select> remains the source of truth and stays in the form. This
 * script hides it once the styled trigger/listbox is active, mirrors the chosen
 * value back into the select, and dispatches the same 'input' event Dawn's
 * facet handler debounces into an AJAX re-sort - so no changes to facets.js.
 *
 * facets.js's renderAdditionalElements() replaces the .sorting element's
 * innerHTML after every AJAX sort, so a MutationObserver re-initialises any
 * freshly rendered combobox.
 */
class SortCombobox {
  constructor(root) {
    this.root = root;
    this.select = root.querySelector('[data-sort-combobox-select]');
    this.trigger = root.querySelector('[data-sort-combobox-trigger]');
    this.listbox = root.querySelector('[data-sort-combobox-listbox]');
    this.label = root.querySelector('[data-sort-combobox-label]');
    this.options = [...root.querySelectorAll('[data-sort-combobox-option]')];

    if (!this.select || !this.trigger || !this.listbox || this.options.length === 0) return;

    this.activeIndex = Math.max(0, this.options.findIndex((o) => o.dataset.value === this.select.value));

    this.onDocumentClick = (event) => {
      if (!this.root.contains(event.target) && this.isOpen) this.close();
    };
    this.onKeyDown = (event) => this.handleKeydown(event);
    this.onPopState = () => this.syncFromURL();

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', this.onKeyDown);
    this.listbox.addEventListener('mousedown', (event) => event.preventDefault());
    this.listbox.addEventListener('click', (event) => {
      const option = event.target.closest('[data-sort-combobox-option]');
      if (option) this.selectOption(this.options.indexOf(option));
    });
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('popstate', this.onPopState);

    this.enhance();
    this.setActive(this.activeIndex, false);
    this.root.setAttribute('data-sort-combobox-initialized', 'true');
  }

  enhance() {
    // Hide the native control from sight while keeping it submittable in the form.
    this.root.classList.add('is-enhanced');
    this.select.setAttribute('tabindex', '-1');
  }

  get isOpen() {
    return this.root.classList.contains('is-open');
  }

  open() {
    if (this.isOpen) return;
    this.root.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.listbox.hidden = false;
    this.setActive(this.activeIndex, false);
  }

  close() {
    if (!this.isOpen) return;
    this.root.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.listbox.hidden = true;
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  setActive(index, scroll = true) {
    this.activeIndex = Math.max(0, Math.min(this.options.length - 1, index));
    this.options.forEach((option, i) => {
      option.classList.toggle('is-active', i === this.activeIndex);
      if (i === this.activeIndex) option.setAttribute('id', 'SortByActiveOption');
      else if (option.id === 'SortByActiveOption') option.removeAttribute('id');
    });
    this.trigger.setAttribute('aria-activedescendant', 'SortByActiveOption');
    if (scroll && this.listbox.hidden === false) {
      const active = this.options[this.activeIndex];
      if (active) active.scrollIntoView({ block: 'nearest' });
    }
  }

  selectOption(index) {
    if (index < 0 || index >= this.options.length) return;
    const option = this.options[index];
    this.select.value = option.dataset.value;
    this.label.textContent = option.textContent.trim();
    this.options.forEach((o) => {
      if (o === option) o.setAttribute('aria-selected', 'true');
      else o.removeAttribute('aria-selected');
    });
    this.close();
    // Dawn's facet handler listens for 'input' on the form; dispatch it so the
    // AJAX re-sort runs exactly as it does for the native control.
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleKeydown(event) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen) this.selectOption(this.activeIndex);
        else this.open();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.isOpen) this.setActive(this.activeIndex + 1);
        else this.open();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) this.setActive(this.activeIndex - 1);
        else this.open();
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.options.length - 1);
        break;
      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close();
          this.trigger.focus();
        }
        break;
      case 'Tab':
        this.close();
        break;
      default:
        // Simple typeahead: jump to the first option starting with the key.
        if (event.key.length === 1) {
          const match = this.options.findIndex((o) =>
            o.textContent.trim().toLowerCase().startsWith(event.key.toLowerCase()),
          );
          if (match !== -1) this.setActive(match);
        }
    }
  }

  syncFromURL() {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('sort_by');
    if (!value) return;
    const index = this.options.findIndex((o) => o.dataset.value === value);
    if (index === -1) return;
    this.select.value = value;
    this.label.textContent = this.options[index].textContent.trim();
    this.setActive(index, false);
  }
}

const initAllComboboxes = () => {
  document
    .querySelectorAll('[data-sort-combobox]:not([data-sort-combobox-initialized])')
    .forEach((root) => new SortCombobox(root));
};

initAllComboboxes();

// facets.js replaces .sorting's innerHTML after every AJAX sort; re-initialise
// the freshly rendered combobox. Cheap: only scans for uninitialised roots.
const comboboxObserver = new MutationObserver(initAllComboboxes);
comboboxObserver.observe(document.body, { childList: true, subtree: true });
